import React, { useState, useEffect, useRef } from 'react';
import { addVideoToDate, listVideosByDate } from '../../../network/service';
import { toast } from "react-toastify";
import Translate from "../../../Components/Multilanguage/Translate";
import { useTranslateText } from "../../../Components/Multilanguage/Translate";

function formatDateForApi(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const CalendarPopup = ({ isOpen, onClose, appInfo, videoId }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [videoMarkedDates, setVideoMarkedDates] = useState(new Set());
  const [addingVideo, setAddingVideo] = useState(false);
  const currentMonthYearRef = useRef(null);

  // Fetch calendar data function similar to Calendar.jsx
  const fetchCalendarData = async (selectedDate, showLoader = true) => {
    try {
      setError(null);
      if (showLoader) setLoading(true);

      const firstDayOfMonth = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        1
      );
      const dayOfWeek = firstDayOfMonth.getDay();

      const firstVisibleDate = new Date(firstDayOfMonth);
      firstVisibleDate.setDate(firstVisibleDate.getDate() - dayOfWeek);

      const lastDayOfMonth = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth() + 1,
        0
      );
      const lastDayOfWeek = lastDayOfMonth.getDay();

      const lastVisibleDate = new Date(lastDayOfMonth);
      lastVisibleDate.setDate(lastVisibleDate.getDate() + (6 - lastDayOfWeek));
      
      const formattedStartDate = formatDateForApi(firstVisibleDate);
      const formattedEndDate = formatDateForApi(lastVisibleDate);

      const response = await listVideosByDate(appInfo, formattedStartDate, formattedEndDate);
      
      if (response?.data?.success) {
        const markedDates = new Set();
        
        response.data.data.forEach((day) => {
          // Check if this date contains our specific video
          const hasVideo = day.videos.some(video => video.video_id === videoId);
          if (hasVideo) {
            markedDates.add(day.date);
          }
        });
        
        setVideoMarkedDates(markedDates);
        setEvents(response.data.data);
      } else {
        setError("Failed to fetch calendar data");
      }
    } catch (err) {
      setError("An error occurred while fetching calendar data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // useEffect to reset states when calendar opens
  useEffect(() => {
    if (isOpen) {
      setSelectedDate(null);
      setEvents([]);
      setVideoMarkedDates(new Set());
      setError(null);
      setCurrentDate(new Date());
      currentMonthYearRef.current = null;
    }
  }, [isOpen]);

  // useEffect to fetch data when month changes
  useEffect(() => {
    if (!isOpen || !appInfo || !videoId) return;
    
    const monthYearKey = `${currentDate.getMonth()}-${currentDate.getFullYear()}`;
    
    if (monthYearKey !== currentMonthYearRef.current) {
      currentMonthYearRef.current = monthYearKey;
      fetchCalendarData(currentDate, true);
    }
  }, [currentDate, isOpen, appInfo, videoId]);

  if (!isOpen) return null;

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const daysOfWeek = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

  const today = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Get first day of the month and how many days in month
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();

  // Get previous month's last days to fill the grid
  const prevMonth = new Date(currentYear, currentMonth, 0);
  const daysInPrevMonth = prevMonth.getDate();

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const handleDateClick = (day, isCurrentMonth = true, isPrevMonth = false, isNextMonth = false) => {
    let newDate;
    if (isPrevMonth) {
      newDate = new Date(currentYear, currentMonth - 1, day);
    } else if (isNextMonth) {
      newDate = new Date(currentYear, currentMonth + 1, day);
    } else {
      newDate = new Date(currentYear, currentMonth, day);
    }
    setSelectedDate(newDate);
  };

  const handleAddVideo = async() => {
    if (!selectedDate) {
      toast.warning("Please select a date", { position: "bottom-left" });
      return;
    }
    
    setAddingVideo(true);
    
    try {
      const res = await addVideoToDate(appInfo, videoId, formatDateForApi(selectedDate));
      
      if (res?.data?.success) {
        toast.success(<Translate textKey="video_added" />, { position: "bottom-left"});
      } else {
        toast.info(res?.message || <Translate textKey={"video_add_failed"} />, { position: "bottom-left" });
      }
    } catch (err) {
      toast.error(<Translate textKey={"video_add_failed"} />, { position: "bottom-left" });
    } finally {
      setAddingVideo(false);
      onClose();
    }
  };

  const isSelectedDate = (day, isCurrentMonth) => {
    if (!isCurrentMonth || !selectedDate) return false;
    return selectedDate.getDate() === day && 
           selectedDate.getMonth() === currentMonth && 
           selectedDate.getFullYear() === currentYear;
  };

  const isToday = (day, isCurrentMonth) => {
    if (!isCurrentMonth) return false;
    return today.getDate() === day && 
           today.getMonth() === currentMonth && 
           today.getFullYear() === currentYear;
  };

  const isVideoMarkedDate = (day, isCurrentMonth = true, isPrevMonth = false, isNextMonth = false) => {
    let checkDate;
    if (isPrevMonth) {
      checkDate = new Date(currentYear, currentMonth - 1, day);
    } else if (isNextMonth) {
      checkDate = new Date(currentYear, currentMonth + 1, day);
    } else {
      checkDate = new Date(currentYear, currentMonth, day);
    }
    const dateString = formatDateForApi(checkDate);
    return videoMarkedDates.has(dateString);
  };

  const renderCalendarDays = () => {
    const days = [];

    // Previous month's trailing days
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      const hasVideoMark = isVideoMarkedDate(day, false, true, false);
      days.push(
        <div
          key={`prev-${day}`}
          className={`calendar-day prev-month ${hasVideoMark ? 'has-video' : ''}`}
          onClick={() => handleDateClick(day, false, true, false)}
        >
          {day}
          {hasVideoMark && <div className="video-mark"></div>}
        </div>
      );
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = isSelectedDate(day, true);
      const isTodayDate = isToday(day, true);
      const hasVideoMark = isVideoMarkedDate(day, true, false, false);
      
      days.push(
        <div
          key={day}
          className={`calendar-day current-month ${isSelected ? 'selected' : ''} ${isTodayDate ? 'today' : ''} ${hasVideoMark ? 'has-video' : ''}`}
          onClick={() => handleDateClick(day, true)}
        >
          {day}
          {hasVideoMark && <div className="video-mark"></div>}
        </div>
      );
    }

    // Next month's leading days to fill the grid
    const totalCells = Math.ceil((startingDayOfWeek + daysInMonth) / 7) * 7;
    const remainingCells = totalCells - (startingDayOfWeek + daysInMonth);
    
    for (let day = 1; day <= remainingCells; day++) {
      const hasVideoMark = isVideoMarkedDate(day, false, false, true);
      days.push(
        <div
          key={`next-${day}`}
          className={`calendar-day next-month ${hasVideoMark ? 'has-video' : ''}`}
          onClick={() => handleDateClick(day, false, false, true)}
        >
          {day}
          {hasVideoMark && <div className="video-mark"></div>}
        </div>
      );
    }

    return days;
  };

  return (
    <div className="calendar-popup-overlay" onClick={onClose}>
      <div className="calendar-popup" onClick={(e) => e.stopPropagation()}>
        {/* Title */}
        <div className="calendar-popup-title">
          <h3><Translate textKey="add_to_calendar" /></h3>
        </div>
        
        {/* Header */}
        <div className="calendar-popup-header">
          <button className="nav-button" onClick={handlePrevMonth}>
            ←
          </button>
          <div className="month-year-display">
            {months[currentMonth]} {currentYear}
          </div>
          <button className="nav-button" onClick={handleNextMonth}>
            →
          </button>
        </div>

        {/* Days of week header */}
        <div className="days-header">
          {daysOfWeek.map((day) => (
            <div key={day} className="day-header">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="calendar-grid">
          {renderCalendarDays()}
          {loading && (
            <div className="calendar-loading-overlay">
              <div className="calendar-loader"></div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="calendar-popup-footer">
          <div className="calendar-popup-actions">
            <button className="calendar-popup-btn calendar-popup-btn--cancel" onClick={onClose}>
              <span>Cancel</span>
            </button>
            <button 
              className="calendar-popup-btn calendar-popup-btn--primary" 
              onClick={handleAddVideo}
              disabled={addingVideo}
            >
              <span><Translate textKey="add_video" /></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarPopup;